import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { DeleteOutlined, EditOutlined, PhoneOutlined } from '@ant-design/icons'
import { Table, Button, Divider, Popconfirm, message } from 'antd'
import { PageHeader } from '../../Common'
import { toPath } from '../../../routes'
import { useAsync, useIsMobile } from '../../../hooks'
import AddNewItemButton from '../../Common/AddNewItemButton'

import {
  useFetchLetterPhoneNumbers,
  useLetterPhoneNumbers,
} from '../../../redux/slices/letter-phone-numbers'
import api from '../../../api'
import PhoneNumberMobileCard from './PhoneNumberMobileRow'

export default function PhoneNumbers() {
  const isMobile = useIsMobile()
  const { isFetching, execute: fetchPhoneNumbers } = useAsync(
    useFetchLetterPhoneNumbers(true),
  )
  const phoneNumbers = useLetterPhoneNumbers()

  const navigate = useNavigate()
  const goToEdit = useCallback(
    id => navigate(toPath.welcomeLetters.phoneNumbers.phoneNumber.edit({ id })),
    [navigate],
  )

  const { pending: isDeleting, execute: handleDelete } = useAsync(async id => {
    try {
      await api.letterPhoneNumbers.deleteLetterPhoneNumber(id)
      message.success('Phone number successfully removed')
    } catch (e) {
      message.error(
        `There was an error removing this phone number: ${e.message}`,
      )
    }

    fetchPhoneNumbers()
  }, false)

  const columns = [
    {
      title: 'Area Code',
      dataIndex: ['areaCode'],
      key: 'areaCode',
    },
    {
      title: 'Phone Number',
      dataIndex: ['phoneNumber'],
      key: 'phoneNumber',
    },
    {
      align: 'center',
      title: 'Actions',
      key: 'actions',
      width: '100px',
      render: (_text, record) => (
        <span>
          <Button
            onClick={() => goToEdit(record.id)}
            type="link"
            icon={<EditOutlined />}
          />
          <Divider type="vertical" />
          <Popconfirm
            title="Are you sure you want to delete this phone number?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            placement="topRight"
          >
            <Button
              type="link"
              icon={<DeleteOutlined />}
              className="delete-button"
            />
          </Popconfirm>
        </span>
      ),
    },
  ]

  // On mobile the whole row collapses into a single card; no expand toggle is
  // needed because every desktop column is shown on the card.
  const mobileColumns = useMemo(
    () => [
      {
        title: 'Phone Numbers',
        key: 'phoneNumber',
        render(_text, record) {
          return (
            <PhoneNumberMobileCard
              letterPhoneNumber={record}
              onEdit={goToEdit}
              onDelete={handleDelete}
            />
          )
        },
      },
    ],
    [goToEdit, handleDelete],
  )

  return (
    <div>
      <PageHeader
        title="Phone Numbers"
        extra={
          <AddNewItemButton
            itemName="phone number"
            path={toPath.welcomeLetters.phoneNumbers.new()}
            icon={<PhoneOutlined />}
          />
        }
      />
      <Table
        size={isMobile ? 'small' : 'middle'}
        rowKey="id"
        columns={isMobile ? mobileColumns : columns}
        showHeader={!isMobile}
        dataSource={phoneNumbers}
        loading={isFetching || isDeleting}
      />
    </div>
  )
}
